import React from "react";
import styled from "styled-components";

import { genBubbleFormatter } from "./bubbleIcons";
const DefaultIcon = genBubbleFormatter();

import { genConnectorFormatter } from "./connector";
const DefaultConnector = genConnectorFormatter();

import Collapsible from "../collapsible";

const FixedTable = styled.table`
  table-layout: fixed;
  height: 1px;
  width: 100%;
`;

const ContentRow = styled.tr`
  vertical-align: top;
`;

const StepLineCell = styled.td`
  width: ${({ width }) => width}px;
  white-space: nowrap;
  height: 100%;
`;

const CenterDiv = styled.div`
  text-align: center;
`;

const FullHeightCenterDiv = styled(CenterDiv)`
  height: 100%;
`;

const Content = styled.div`
  margin: 10px 0;
  text-align: left;

  font-family: ${({ theme }) => theme.fonts.family.bookText};
  font-weight: ${({ theme }) => theme.fonts.weight.bookText};
  font-size: ${({ theme }) => theme.fonts.sizes.Text4};
`;

const passDefaultsToChild = (child, defaults = {}) =>
  React.cloneElement(child, {
    ...defaults,
    ...child.props
  });

const DefaultLabelCell = styled.td`
  max-height: 20px;
  text-align: left;
  font-family: ${({ theme, active }) => (active ? theme.fonts.family.boldText : theme.fonts.family.bookText)};
  font-weight: ${({ theme, active }) => (active ? theme.fonts.weight.boldText : theme.fonts.weight.bookText)};
  font-size: ${({ theme }) => theme.fonts.sizes.Text4};
`;

const DefaultContentCell = ({ showContent, children } = {}) => (
  <td>
    <Content>
      <Collapsible
        show={showContent}
        openEasing="ease-in"
        closeEasing="ease-out"
      >
        {children}
      </Collapsible>
    </Content>
  </td>
);

const DefaultIconCell = ({ stepColWidth, children } = {}) => (
  <StepLineCell width={stepColWidth}>
    <CenterDiv>
      {children}
    </CenterDiv>
  </StepLineCell>
);

const DefaultConnectorCell = (
  { stepColWidth, onEndBranch, hasSubSections, active, children } = {}
) => (
  <StepLineCell width={stepColWidth}>
    <FullHeightCenterDiv>
      <Collapsible
        openHeight="100%"
        show={!onEndBranch || (hasSubSections && active)}
        openDuration="0.2s"
        closeDuration="0.3s"
        closeDelay="0.2s"
        closeEasing={[0.7, 0.05, 0.92, 0.73]}
      >
        {children}
      </Collapsible>
    </FullHeightCenterDiv>
  </StepLineCell>
);

function Step(props = {}) {
  const {
    stepColWidth,
    index = 0,
    _onEndBranch,
    active,
    complete,
    iconRender: Icon = DefaultIcon,
    iconCellRender: IconCell = DefaultIconCell,
    connectorRender: Connector = DefaultConnector,
    connectorCellRender: ConnectorCell = DefaultConnectorCell,
    label = "",
    labelCellRender: LabelCell = DefaultLabelCell,
    showContent = true,
    contentCellRender: ContentCell = DefaultContentCell,
    children
  } = props;

  const content = [];
  const subSections = [];

  React.Children.forEach(children, (child, childIndex) => {
    if (child.type === SubSection || (child.type && child.type.isSubSection)) {
      subSections.push(
        passDefaultsToChild(child, {
          stepColWidth,
          key: childIndex,
          show: active,
          _onEndBranch
        })
      );
    } else {
      content.push(child);
    }
  });

  return (
    <tbody>
      <tr key={`${index}-label`}>
        <IconCell
          stepColWidth={stepColWidth}
          stepIndex={index}
          active={active}
          complete={complete}
        >
          <Icon index={index} complete={complete} active={active} />
        </IconCell>
        <LabelCell active={active}>
          {label}
        </LabelCell>
      </tr>
      <ContentRow key={`${index}-content`}>
        <ConnectorCell
          stepColWidth={stepColWidth}
          onEndBranch={_onEndBranch}
          hasSubSections={subSections.length > 0}
          active={active}
          complete={complete}
        >
          <Connector complete={complete} />
        </ConnectorCell>
        <ContentCell showContent={showContent}>
          {content}
        </ContentCell>
      </ContentRow>
      {subSections}
    </tbody>
  );
}

const renderSteps = (steps, sectionOnEndBranch, additionalProps = {}) => {
  const stepsCount = React.Children.count(steps);
  return React.Children.map(steps, (step, index) =>
    passDefaultsToChild(step, {
      index,
      _onEndBranch: sectionOnEndBranch && index + 1 === stepsCount,
      ...additionalProps
    })
  );
};

function SubSection(props = {}) {
  const { _onEndBranch, stepColWidth, show, children } = props;

  return (
    <tr>
      <td colSpan={2}>
        <Collapsible
          openEasing={[0.7, 0.05, 0.92, 0.73]}
          closeEasing={[0.24, 0.56, 0.65, 0.96]}
          show={show}
        >
          <FixedTable>
            {renderSteps(children, _onEndBranch, { stepColWidth })}
          </FixedTable>
        </Collapsible>
      </td>
    </tr>
  );
}

function VerticalSteps(props = {}) {
  const { stepColWidth = 40, children } = props;

  return (
    <FixedTable>
      {renderSteps(children, true, { stepColWidth })}
    </FixedTable>
  );
}

VerticalSteps.Step = Step;
VerticalSteps.SubSection = SubSection;

export default VerticalSteps;



// WEBPACK FOOTER //
// ./src/common/verticalSteps/verticalSteps.js